import React from 'react';
import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import { Close, AddCircleOutline } from '@mui/icons-material';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { CurrencyInputField, FormRadioGroup, FormSwitch, InputField, InterestInputField } from 'components/form';
import { FinanceProgramFormValues, MarkupType } from '../../../../../../types';
import { markupTypesOptions } from 'modules/desking/constants';
import { useSelector } from 'react-redux';
import { deskingSelectors } from 'modules/desking/model';

export const FeeArrayFields = () => {
  const classes = useStyles();

  const isFinanceDetailsEditable = useSelector(deskingSelectors.selectFinanceDetailsEditableStatus);

  const { control } = useFormContext<FinanceProgramFormValues>();

  const { remove, append, fields, update } = useFieldArray({ control, name: 'financeQuote.fee' });

  const watchFields = useWatch({
    control,
    name: 'financeQuote.fee',
  });

  return (
    <Box className={classes.arrayContainer}>
      {!!fields?.length &&
        fields.map((feeField, index) => (
          <Box key={feeField.id}>
            <Box className={classes.array}>
              <Typography component="span" variant="subtitle2">
                Fee: {index + 1}
              </Typography>

              <Box className={clsx('row', classes.arrayFields)}>
                <InputField
                  control={control}
                  label="Description"
                  name={`financeQuote.fee.${index}.identifier`}
                  inputProps={{ disabled: !isFinanceDetailsEditable }}
                />
                <Box>
                  {feeField.type === MarkupType.Flat ? (
                    <CurrencyInputField
                      control={control}
                      label="Fee ($)"
                      name={`financeQuote.fee.${index}.fee`}
                      textFieldProps={{ disabled: !isFinanceDetailsEditable }}
                    />
                  ) : (
                    <InterestInputField
                      label="Fee (%)"
                      control={control}
                      name={`financeQuote.fee.${index}.fee`}
                      textFieldProps={{ disabled: !isFinanceDetailsEditable }}
                    />
                  )}
                  <Controller
                    control={control}
                    name={`financeQuote.fee.${index}.type`}
                    render={({ field }) => (
                      <FormRadioGroup
                        row
                        options={markupTypesOptions}
                        {...field}
                        disabled={!isFinanceDetailsEditable}
                        onChange={(event, value) =>
                          update(index, {
                            ...watchFields[index],
                            type: value as MarkupType,
                          })
                        }
                      />
                    )}
                  />
                </Box>
              </Box>

              <IconButton size="small" onClick={() => remove(index)} disabled={!isFinanceDetailsEditable}>
                <Close fontSize="small" />
              </IconButton>
            </Box>

            <Controller
              control={control}
              name={`financeQuote.fee.${index}.financed`}
              render={({ field }) => (
                <FormSwitch
                  label="Financed?"
                  defaultChecked={field.value ? field.value : false}
                  {...field}
                  disabled={!isFinanceDetailsEditable}
                />
              )}
            />
          </Box>
        ))}

      <Box>
        <Button
          size="small"
          color="primary"
          variant="outlined"
          startIcon={<AddCircleOutline />}
          disabled={!isFinanceDetailsEditable}
          onClick={() => append({ financed: true, fee: 0, identifier: '', type: MarkupType.Flat })}
        >
          Add Fee
        </Button>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  arrayContainer: {
    gap: '12px',
    display: 'flex',
    flexDirection: 'column',
  },
  array: {
    gap: '12px',
    display: 'flex',
    alignItems: 'baseline',
  },
  arrayFields: {
    flex: 1,
  },
});
